import { motion, AnimatePresence, Variants } from "motion/react";
import { CSSProperties, useState, useEffect, useRef } from "react";
import Counter from "../Counter/Counter";
interface LoadingOverlayProps {
  isLoading: boolean;
  backgroundColor?: string;
  barColor?: string;
  transitionDuration?: number;
  zIndex?: number;
  style?: CSSProperties;
  children?: React.ReactNode;
}

export const LoadingOverlay = ({
  isLoading,
  backgroundColor = "rgba(0, 0, 0, 1)",
  barColor = "#ffffff",
  zIndex = 9999,
  style,
  children,
}: LoadingOverlayProps) => {
  const [showOverlay, setShowOverlay] = useState(isLoading);
  // Progress should reflect the bar's animated height
  // We'll use a ref to track the current height of the bar and update progress accordingly
  const [progress, setProgress] = useState(0);
  const barRef = useRef<HTMLDivElement>(null);

  // Helper to extract the current height in vh from the bar's inline style
  function getBarHeightPercent() {
    if (!barRef.current) return 0;
    const height = barRef.current.style.height;
    if (!height.endsWith("vh")) return 0;
    const vh = parseFloat(height);
    // Clamp to [0, 100]
    return Math.max(0, Math.min(100, Math.round(vh)));
  }

  useEffect(() => {
    let frame: number;
    function updateProgressFromBar() {
      // Get the current height of the bar in vh (0-100)
      const percent = getBarHeightPercent();
      setProgress(percent);
      if (isLoading) {
        frame = requestAnimationFrame(updateProgressFromBar);
      } else {
        setProgress(99);
      }
    }
    if (isLoading) {
      frame = requestAnimationFrame(updateProgressFromBar);
    } else {
      setProgress(99);
    }
    return () => cancelAnimationFrame(frame);
  }, [isLoading]);

  useEffect(() => {
    if (isLoading) {
      setShowOverlay(true);
    }
  }, [isLoading]);

  const handleAnimationComplete = () => {
    if (!isLoading) {
      setShowOverlay(false);
    }
  };

  // Generate random stepwise heights for the loading animation
  function getStepwiseHeights() {
    // You can adjust the number of steps and their randomness as needed
    const steps = 3 + Math.floor(Math.random() * 2); // 3 or 4 steps
    const heights = [];
    for (let i = 1; i <= steps; i++) {
      // Random height between 10vh and 55vh, increasing with each step
      const min = 10 * i;
      const max = Math.min(10 * (i + 1), 55);
      heights.push(`${Math.floor(Math.random() * (max - min + 1) + min)}vh`);
    }
    return heights;
  }

  // Memoize the random steps so they don't change on every render
  const [stepHeights] = useState(() => getStepwiseHeights());

  const barVariants: Variants = {
    initial: {
      height: "0vh",
      width: "2px",
      x: "0%",
    },
    loading: {
      height: ["0vh", ...stepHeights, "60vh"],
      width: "2px",
      x: "0%",
      transition: {
        height: { duration: 1.2, times: [0, 0.3, 0.6, 1], ease: "easeInOut" },
      },
    },
    loaded: {
      height: "100vh",
      width: "2px",
      x: "0%",
      transition: {
        height: { duration: 0.3, ease: "easeInOut" },
        width: { duration: 0.5, ease: "easeInOut", delay: 0.3 },
      },
    },
    exit: (index: number) => ({
      x: index === 0 ? "-100vw" : "100vw",
      transition: {
        duration: 0.8,
        ease: "easeInOut",
      },
    }),
  };

  return (
    <AnimatePresence>
      {showOverlay && (
        <motion.div
          initial={{ backdropFilter: "blur(8px)" }}
          animate={{ backdropFilter: "blur(8px)" }}
          exit={{ backdropFilter: "blur(0px)" }}
          transition={{ backdropFilter: { duration: 0.8, ease: "easeInOut" } }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex,
            pointerEvents: "auto",
            ...style,
          }}
        >
          {/* Left Panel */}
          <motion.div
            initial={{ x: 0 }}
            animate={{ x: 0 }}
            exit={{ x: "-100vw" }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "50vw",
              height: "100vh",
              background: backgroundColor,
              zIndex: 1,
            }}
          />
          {/* Right Panel */}
          <motion.div
            initial={{ x: 0 }}
            animate={{ x: 0 }}
            exit={{ x: "100vw" }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "50vw",
              height: "100vh",
              background: backgroundColor,
              zIndex: 1,
            }}
          />
          <div
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
              zIndex: 2,
            }}
          >
            {[0, 1].map((index) => (
              <motion.div
                key={index}
                custom={index}
                initial="initial"
                animate={!isLoading ? "loaded" : "loading"}
                exit="exit"
                variants={barVariants}
                onAnimationComplete={handleAnimationComplete}
                style={{
                  position: "absolute",
                  background: barColor,
                  transformOrigin: "center",
                  zIndex: 3,
                }}
                ref={index === 0 ? barRef : undefined} // Attach ref to the first bar only
              />
            ))}
            {/* Centered Circle with Counter and Progress */}
            <motion.div
              initial={{ scale: 0.5, opacity: 1, x: "-50%", y: "-50%" }}
              animate={{
                scale: 1,
                opacity: 1,
                x: "-50%",
                y: "-50%",
                transition: {
                  scale: {
                    type: "spring",
                    stiffness: 400,
                    damping: 30,
                    duration: 1,
                  },
                  opacity: { duration: 1, ease: "easeOut" },
                  delay: 0.1,
                },
              }}
              exit={{
                scale: 0,
                opacity: 0,
                x: "-50%",
                y: "-50%",
                transition: {
                  scale: {
                    type: "spring",
                    stiffness: 300,
                    damping: 25,
                    duration: 1,
                  },
                  opacity: { duration: 0.3, ease: "easeIn" },
                },
              }}
              className="bg-red-600 rounded-full"
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                zIndex: 5,
                width: 80,
                height: 80,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {/* <svg width={120} height={120}>
                <circle
                  cx={60}
                  cy={60}
                  r={54}
                  stroke="#444"
                  strokeWidth={8}
                  fill="none"
                  opacity={0.2}
                />
                <motion.circle
                  cx={60}
                  cy={60}
                  r={54}
                  stroke={barColor}
                  strokeWidth={8}
                  fill="none"
                  strokeDasharray={2 * Math.PI * 54}
                  animate={{
                    strokeDashoffset: 2 * Math.PI * 54 * (1 - progress / 100),
                  }}
                  initial={false}
                  transition={{
                    strokeDashoffset: {
                      type: "tween",
                      ease: "easeInOut",
                      duration: 0.7,
                    },
                  }}
                  style={{ rotate: -90, originX: "60px", originY: "60px" }}
                />
              </svg> */}
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: 80,
                  height: 80,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 32,
                  fontWeight: 700,
                  color: barColor,
                  background: "transparent",
                }}
              >
                <Counter
                  number={progress}
                  duration={0.3}
                  className="text-white"
                />
              </div>
            </motion.div>
            <div
              style={{
                position: "relative",
                zIndex: 4,
              }}
            >
              {children}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
